import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ElementRef,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { NavigationService } from '../../services/navigation.service';
import { ApiService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { ProfileService } from '../../services/profile.service';
import { VendorModeService } from '../../services/vendor-mode.service';
import { CommonModule } from '@angular/common';
import { ProfileDropdownComponent } from '../../components/profile-dropdown/profile-dropdown.component';
import { NotificationDropdownComponent } from '../../components/notification-dropdown/notification-dropdown.component';
import {
  GateButtonAction,
  GateButtonComponent,
} from '../components/gate-button/gate-button.component';
import { LaneSelectorComponent } from '../components/lane-selector/lane-selector.component';
import { UserProfile } from '../types/user.interface';
import { Subscription, Subject, asyncScheduler, forkJoin, of } from 'rxjs';
import { catchError, filter, finalize, switchMap, throttleTime } from 'rxjs/operators';
import { RoleService } from '../../services/role.service';
import { LocationService } from '../../services/location.service';
import { NotificationService, NotificationData } from '../../services/notification.service';
import { RealtimeEventsService } from '../../services/realtime-events.service';
import { Router, NavigationEnd } from '@angular/router';
import { ModalStateService } from '../../services/modal-state.service';
import { WayPurchaseModalStateService } from '../../components/way-purchase-modal/services/way-purchase-modal-state.service';
import { MultiEligibleSelectionModalStateService } from '../../components/multi-eligible-selection-modal/services/multi-eligible-selection-modal-state.service';
import { openStaffQuickSaleModal } from '../utils/notification-modal-open.util';
import {
  getUserDataLaneCameraRoi,
  getUserDataLaneId,
  getUserDataLocationId,
  setUserDataLaneId,
  setUserDataLocationId,
} from '../utils/auth-headers.util';
import {
  buildHeaderNotificationBadgeLprQueryParams,
  countUnreadNotificationListItems,
  headerDropdownNotificationsCache,
  isLprEntryMarkedRead,
  mergeKnownReadStateIntoNotificationList,
  isLprEntryUpdatedPusherNotification,
  NOTIFICATION_READ_STATE_CHANGED_EVENT,
  resolveLprEntryIdFromNotificationData,
  sortNotificationListItemsByTimestamp,
  transformLprEntriesResponseToNotificationList,
} from '../../notifications/notification.utils';

const GATE_COMMAND_TEXT: Record<
  GateButtonAction,
  { success: string; failure: string; progress: string }
> = {
  open: { success: 'Gate opened', failure: 'Open gate failed', progress: 'opening' },
  close: { success: 'Closing gate', failure: 'Close gate failed', progress: 'closing' },
};

interface HeaderLane {
  id: string;
  name: string;
  status?: string;
  camera_roi?: number | null;
  gate_serial?: string | null;
  gate_relay?: number | null;
  /** CWS-1714: set when the lane has a manual Close gate relay. */
  gate_close_relay?: number | null;
}

/**
 * Header component that presents the application top bar including navigation toggle, user profile area, and notification bell; it manages user profile display, notification counting, and interactions with dropdowns and related services.
 * @component
 * @selector app-header
 * @example
 *   <app-header></app-header>
 * @input none - This component does not expose @Input properties; it obtains data via injected services (ProfileService, NavigationService, NotificationService, etc.).
 * @output none - This component does not emit @Output events; it communicates via services, subscriptions, and a global custom event ("notificationsMarkedAsViewed").
 *
 * Purpose:
 *   - Maintain and render header state (mobile/desktop nav, sidebar collapsed state, profile and notification dropdown visibility).
 *   - Display user avatar or initials using profile data from ProfileService.
 *   - Track and display the number of unseen notifications by comparing API timestamps to a persisted "last viewed" timestamp.
 *
 * Key methods:
 *   - toggleMobileNav(): Delegates to NavigationService to open/close mobile navigation.
 *   - toggleProfileDropdown(): Toggle profile dropdown visibility and ensure notification dropdown is closed when opened.
 *   - toggleNotificationDropdown(event?): Toggle notification dropdown (desktop or mobile behavior); prevents event propagation, coordinates with profile dropdown, and syncs counts when closing.
 *   - checkForNewNotifications(): Query notifications for the user's locations, aggregate timestamps, and update notificationCount relative to the stored last viewed timestamp; guards against concurrent loads.
 *   - markNotificationsAsViewed(): Persist current timestamp to localStorage and clear badge count; intended to be called when the dropdown component marks notifications viewed.
 *   - syncNotificationCount(): Compare localStorage timestamp to in-memory value and refresh counts if changed.
 *   - getProfileImage(): Return image URL or fallback avatar path.
 *   - initials (getter): Compute user initials for avatar fallback.
 *   - hasProfileImage (getter): Return whether a valid profile image URL exists.
 *   - getLocationIds(): Helper that uses LocationService to obtain the user's location IDs (private).
 *   - handleNotificationsMarkedAsViewed(event: CustomEvent): Internal handler for a global "notificationsMarkedAsViewed" event that updates the in-memory lastViewedTimestamp and clears the badge.
 *   - onDocumentClick(event): Host-listener method that closes dropdowns when clicking outside and synchronizes notification counts as needed.
 *   - subscribeToProfileChanges() / loadUserProfile(): Wire up profile subscriptions and trigger lazy profile loading.
 *
 * Lifecycle hooks:
 *   - ngOnInit(): Subscribes to profile changes, loads user profile if needed, and initiates notification checking.
 *   - ngOnDestroy(): Unsubscribes from all subscriptions and removes the window-level event listener for notificationsMarkedAsViewed.
 *
 * Important notes:
 *   - The component relies heavily on injected services for navigation state, profile data, API requests, location resolution, and incoming push notifications.
 *   - Notification counting uses localStorage key "notification_last_viewed" to persist the timestamp of when notifications were last acknowledged.
 *   - The component sets up a global window event listener for "notificationsMarkedAsViewed" dispatched by child dropdown components; it cleans this up on destroy.
 */
/** Min gap between API reconciles when the stream keeps reconnecting (a flapping tablet). */
const RECONNECT_RECONCILE_THROTTLE_MS = 10_000;

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    ProfileDropdownComponent,
    NotificationDropdownComponent,
    GateButtonComponent,
    LaneSelectorComponent,
  ],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild(LaneSelectorComponent) laneSelector?: LaneSelectorComponent;
  private readonly realtime = inject(RealtimeEventsService);

  isMobileNavOpen = false;
  isSidebarCollapsed = false;
  isProfileDropdownOpen = false;
  isNotificationDropdownOpen = false;
  isMobileNotificationDropdownOpen = false;
  userProfile: UserProfile | null = null;
  notificationCount = 0;
  /** Used so template updates when route changes. */
  headerRoutePath = '';
  selectedCurrentLane = '';
  currentLaneOptions: string[] = [];
  /** In-flight manual gate command per lane id (one at a time: open or close). */
  private gateCommandsInFlight = new Map<string, GateButtonAction>();
  private currentLanes: HeaderLane[] = [];
  private gateLocationId = '';
  private subscriptions: Subscription = new Subscription();
  /**
   * Serializes selected-lane persistence: rapid lane changes push here and `switchMap`
   * cancels any in-flight PATCH so only the latest selection is written (last-write-wins),
   * preventing an out-of-order response from persisting a stale lane.
   */
  private readonly persistLaneRequests$ = new Subject<string | null>();
  private isLoadingNotifications = false;
  private notificationReadStateChangedHandler: ((event: Event) => void) | null = null;

  /**
   * Component constructor that initializes header state, sets up service subscriptions, loads the last viewed notification timestamp, and registers a notifications viewed event handler.
   * @example
   * new HeaderComponent(navigationService, apiService, authService, profileService, elementRef, locationService, notificationService)
   * HeaderComponent instance
   * @param {{NavigationService}} {{navigationService}} - Service that provides navigation state (mobile nav open and sidebar collapsed observables).
   * @param {{ApiService}} {{apiService}} - Service for making API calls.
   * @param {{AuthService}} {{authService}} - Service that provides authentication and user context.
   * @param {{ProfileService}} {{profileService}} - Service to manage user profile data.
   * @param {{ElementRef}} {{elementRef}} - Reference to the host DOM element for this component.
   * @param {{LocationService}} {{locationService}} - Service for location-related utilities.
   * @param {{NotificationService}} {{notificationService}} - Service that emits notification events and provides notification-related methods.
   * @returns {{void}} Constructs and initializes the header component instance.
   **/
  constructor(
    private navigationService: NavigationService,
    private apiService: ApiService,
    private authService: AuthService,
    private profileService: ProfileService,
    private elementRef: ElementRef,
    private roleService: RoleService,
    private locationService: LocationService,
    private notificationService: NotificationService,
    private router: Router,
    private modalStateService: ModalStateService,
    private wayPurchaseModalState: WayPurchaseModalStateService,
    private multiEligibleModalState: MultiEligibleSelectionModalStateService,
    private messageService: NotificationService,
    private vendorModeService: VendorModeService
  ) {
    this.subscriptions.add(
      this.navigationService.isMobileNavOpen$.subscribe(isOpen => {
        this.isMobileNavOpen = isOpen;
      })
    );

    this.subscriptions.add(
      this.navigationService.isSidebarCollapsed$.subscribe(collapsed => {
        this.isSidebarCollapsed = collapsed;
      })
    );

    this.headerRoutePath = this.router.url.split('?')[0] || '';
    this.subscriptions.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => {
          this.headerRoutePath = this.router.url.split('?')[0] || '';
        })
    );

    // Subscribe to real-time pushes to refresh badge (ignore BehaviorSubject initial `null`
    // so we do not double-fetch with ngOnInit's checkForNewNotifications).
    this.subscriptions.add(
      this.notificationService.notification$
        .pipe(filter((n): n is NotificationData => n != null))
        .subscribe(notification => {
          const data = notification.data as Record<string, any> | undefined;
          if (isLprEntryUpdatedPusherNotification(notification)) {
            headerDropdownNotificationsCache.upsertFromNotification(notification);
            return;
          }
          const hasLprEntry = !!resolveLprEntryIdFromNotificationData(data);
          // Lane events open the modal which hydrates from API — bump badge and sync dropdown cache.
          if (hasLprEntry) {
            this.bumpNotificationBadgeForPusher(data);
            headerDropdownNotificationsCache.upsertFromNotification(notification);
            return;
          }
          this.checkForNewNotifications();
        })
    );

    // CWS-1575: svc-sse has no Last-Event-ID replay, so notifications published while the stream
    // was down (watchdog, session re-auth, network blip) never reach this tablet. Rebuild the
    // bell list and badge from the API whenever the stream comes back. Leading + trailing so a
    // flapping connection costs at most one fetch per window and still ends on a fresh state.
    this.subscriptions.add(
      this.realtime.ready$
        .pipe(
          filter(ready => ready.reconnect),
          throttleTime(RECONNECT_RECONCILE_THROTTLE_MS, asyncScheduler, {
            leading: true,
            trailing: true,
          })
        )
        .subscribe(() => {
          this.reloadNotificationsFromApi(
            getUserDataLaneId() ?? null,
            getUserDataLaneCameraRoi() ?? null
          );
        })
    );

    this.subscriptions.add(
      this.notificationService.lprEntryUpdatedNotification$.subscribe(value => {
        if (!value) return;
        const { notification } = value;
        headerDropdownNotificationsCache.upsertFromNotification(notification);
        const data = notification.data as Record<string, any> | undefined;
        if (data) {
          this.bumpNotificationBadgeForPusher(data);
        }
      })
    );

    // Cross-device/cross-tab lane sync: when this user changes their lane on another session,
    // reconcile the header selection + lane-dependent UI here (idempotent for our own echo).
    // The pushed location is applied first so a change made in a different location realigns
    // this session before the lane is resolved.
    this.subscriptions.add(
      this.notificationService.selectedLaneChanged$.subscribe(event => {
        this.applyRemoteSelectedLane(event?.selected_lane_id ?? null, event?.location_id ?? null);
      })
    );

    // Serialize selected-lane persistence so rapid lane switches can't race: switchMap
    // cancels the previous in-flight PATCH, guaranteeing only the latest lane is persisted.
    this.subscriptions.add(
      this.persistLaneRequests$
        .pipe(
          switchMap(laneId =>
            this.apiService.updateSelectedLane(laneId).pipe(
              catchError(error => {
                console.error('Error persisting selected lane:', error);
                return of(null);
              })
            )
          )
        )
        .subscribe()
    );

    // Listen for per-notification read updates from the dropdown (badge only — no list reload).
    if (typeof window !== 'undefined') {
      this.notificationReadStateChangedHandler = (event: Event) => {
        const customEvent = event as CustomEvent<{ unreadDelta?: number }>;
        this.handleNotificationReadStateChanged(customEvent);
      };
      window.addEventListener(
        NOTIFICATION_READ_STATE_CHANGED_EVENT,
        this.notificationReadStateChangedHandler
      );
    }
  }

  ngOnInit(): void {
    this.locationService.loadLocations();
    this.subscribeToProfileChanges();
    this.loadUserProfile();
    this.loadHeaderLanes();
    this.subscriptions.add(
      this.locationService.locations$.subscribe(() => {
        const nextLocationId = this.resolveGateLocationId();
        if (nextLocationId && nextLocationId !== this.gateLocationId) {
          this.loadHeaderLanes();
        }
      })
    );
    this.checkForNewNotifications();
  }

  get showLaneSelector(): boolean {
    if (this.roleService.isStaff()) {
      return false;
    }
    return this.currentLanes.length > 1;
  }

  /**
   * The currently selected lane, but only when it is wired with a usable gate.
   * Drives the Open gate / Close gate buttons: when null, they are hidden.
   */
  get selectedGateLane(): HeaderLane | null {
    if (this.roleService.isStaff()) {
      return null;
    }
    const lane = this.getSelectedLane();
    return lane && this.laneHasGate(lane) ? lane : null;
  }

  /** A lane is openable when it is active and wired with a gate serial + relay. */
  private laneHasGate(lane: HeaderLane): boolean {
    const isActive = String(lane.status ?? 'active').toLowerCase() === 'active';
    const hasSerial = !!String(lane.gate_serial ?? '').trim();
    const hasRelay = lane.gate_relay != null;
    return isActive && hasSerial && hasRelay;
  }

  /** Gate buttons or the lane selector are shown (on phones they take a second header row). */
  get hasGateControls(): boolean {
    return !!this.selectedGateLane || this.showLaneSelector;
  }

  /** The Close gate button shows only for lanes with a close relay (off by default). */
  laneCanClose(lane: HeaderLane): boolean {
    return lane.gate_close_relay != null;
  }

  /**
   * Whether the lane has a manual gate command in flight — any command, or only
   * `action` when given. Both buttons are disabled while either one is sending.
   */
  isLaneGateBusy(laneId: string, action?: GateButtonAction): boolean {
    const inFlight = this.gateCommandsInFlight.get(laneId);
    return action ? inFlight === action : inFlight !== undefined;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    // Remove event listener
    if (typeof window !== 'undefined' && this.notificationReadStateChangedHandler) {
      window.removeEventListener(
        NOTIFICATION_READ_STATE_CHANGED_EVENT,
        this.notificationReadStateChangedHandler
      );
      this.notificationReadStateChangedHandler = null;
    }
  }

  subscribeToProfileChanges(): void {
    this.subscriptions.add(
      this.profileService.userProfile$.subscribe(profile => {
        this.userProfile = profile;
      })
    );
  }

  loadUserProfile(): void {
    // Use ProfileService to load profile if needed (prevents duplicate API calls)
    this.profileService.loadUserProfileIfNeeded();
    // Same shell hook loads the shop's vendor-mode flags — this app has no
    // APP_INITIALIZER, so the shell is where app-wide state gets populated.
    this.vendorModeService.loadIfNeeded();
  }

  toggleMobileNav() {
    this.navigationService.toggleMobileNav();
  }

  isOrdersHeaderLayout(): boolean {
    const p = this.headerRoutePath || this.router.url.split('?')[0] || '';
    return p.startsWith('/orders') && !p.startsWith('/orders/');
  }

  showHeaderLprScanButton(): boolean {
    return this.roleService.isShopFloorEmployee() || this.roleService.isAdmin();
  }

  /** Notification center: admin + attendant. Staff (attendance-only) must not see it. */
  showNotificationBell(): boolean {
    return this.roleService.isAdmin() || this.roleService.isShopFloorEmployee();
  }

  /** Header "+" quick sale: admin + attendant only. */
  showHeaderAddOrderButton(): boolean {
    return this.roleService.isAdmin() || this.roleService.isShopFloorEmployee();
  }

  onHeaderQuickPlus(event: Event): void {
    event.stopPropagation();
    if (!this.showHeaderAddOrderButton()) {
      return;
    }
    this.closeHeaderMenus();
    openStaffQuickSaleModal(
      this.modalStateService,
      this.wayPurchaseModalState,
      this.multiEligibleModalState
    );
  }

  onHeaderLprScan(event: Event): void {
    event.stopPropagation();
    if (!this.showHeaderLprScanButton()) {
      return;
    }
    this.closeHeaderMenus();
    if (typeof window === 'undefined') {
      return;
    }

    if (this.isOrdersHeaderLayout()) {
      window.dispatchEvent(new Event('way-header-lpr-capture'));
      return;
    }

    void this.router.navigate(['/orders']).then(ok => {
      if (!ok || typeof window === 'undefined') return;
      setTimeout(() => window.dispatchEvent(new Event('way-header-lpr-capture')), 50);
    });
  }

  private closeHeaderMenus(): void {
    this.isProfileDropdownOpen = false;
    this.isNotificationDropdownOpen = false;
    this.isMobileNotificationDropdownOpen = false;
    this.laneSelector?.close();
    if (this.isMobileNavOpen) {
      this.navigationService.setMobileNavOpen(false);
    }
  }

  onCurrentLaneChange(lane: string): void {
    this.selectedCurrentLane = lane;
    const selectedLane = this.getSelectedLane();
    const laneId = selectedLane?.id ?? null;
    const cameraRoi =
      selectedLane?.camera_roi != null && Number.isInteger(Number(selectedLane.camera_roi))
        ? Number(selectedLane.camera_roi)
        : null;
    setUserDataLaneId(laneId ?? undefined, cameraRoi);
    // Local change persists to the backend, which fans a `selected-lane-changed` Pusher
    // event back out to this user's OTHER sessions (this tab ignores its own echo).
    this.persistSelectedLane(laneId);
    this.afterSelectedLaneChanged(laneId, cameraRoi);
  }

  /**
   * Shared downstream refresh path for BOTH a local lane change and a remote lane
   * change (Pusher or per-user SSE).
   */
  private afterSelectedLaneChanged(laneId: string | null, cameraRoi: number | null): void {
    this.reloadNotificationsFromApi(laneId, cameraRoi);
  }

  /**
   * Rebuild the notification UI from the API: invalidate the header dropdown cache, notify
   * the notification dropdown via the `way-selected-lane-changed` window event (it reloads
   * its list when open), and refresh the header notification badge (keyed off the
   * current/header lane). Used after a lane change and after a realtime stream reconnect.
   */
  private reloadNotificationsFromApi(laneId: string | null, cameraRoi: number | null): void {
    headerDropdownNotificationsCache.invalidate();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('way-selected-lane-changed', {
          detail: { laneId, cameraRoi },
        })
      );
    }
    this.checkForNewNotifications();
  }

  /**
   * Apply a selected-lane change pushed from another of this user's sessions
   * (per-user SSE / Pusher).
   *
   * Idempotent by design: if the incoming lane (and location) already match what is stored
   * (the common case on the tab that initiated the change and received its own echo) this is
   * a no-op, so we never thrash the badge or loop back into a persist/reload. When the pushed
   * lane is not in the currently loaded list we persist the id locally and reload the header
   * lanes so the selector can resolve its name + camera ROI. NOTE: this deliberately does NOT
   * call `persistSelectedLane` — the change is already persisted server-side, and re-PATCHing
   * would create a feedback loop.
   *
   * The pushed `location_id` is honored first: when another session changed lane in a
   * DIFFERENT location, we realign this session's location before resolving the lane so the
   * correct location's lanes load and the pushed lane can actually be found.
   */
  private applyRemoteSelectedLane(laneId: string | null, locationId: string | null = null): void {
    const normalized =
      laneId != null && String(laneId).trim() !== '' ? String(laneId).trim() : null;
    const normalizedLocation =
      locationId != null && String(locationId).trim() !== '' ? String(locationId).trim() : null;
    const currentLaneId = getUserDataLaneId() ?? null;
    const currentLocationId = getUserDataLocationId() ?? null;

    // Realign the session's location first when the remote change came from a different
    // location, so lane resolution + lane loading below target the correct location.
    const locationChanged = normalizedLocation != null && normalizedLocation !== currentLocationId;
    if (locationChanged) {
      setUserDataLocationId(normalizedLocation);
    }

    // Echo / already in sync (same lane AND same location): nothing to do.
    if (!locationChanged && currentLaneId === normalized) {
      return;
    }

    if (!normalized) {
      this.selectedCurrentLane = '';
      setUserDataLaneId(undefined, null);
      if (locationChanged) {
        // Reload so the selector reflects the new location's lanes (with none selected).
        this.loadHeaderLanes();
      } else {
        this.afterSelectedLaneChanged(null, null);
      }
      return;
    }

    // After a location change the current list belongs to the old location, so always reload.
    const lane = locationChanged ? undefined : this.currentLanes.find(l => l.id === normalized);
    if (!lane) {
      // Lane belongs to a location/list not loaded here yet: store the id first so the
      // reload's applyLoadedLanes() selects it and resolves its camera ROI, then reload.
      setUserDataLaneId(normalized, null);
      this.afterSelectedLaneChanged(normalized, null);
      this.loadHeaderLanes();
      return;
    }

    const cameraRoi =
      lane.camera_roi != null && Number.isInteger(Number(lane.camera_roi))
        ? Number(lane.camera_roi)
        : null;
    this.selectedCurrentLane = lane.name;
    setUserDataLaneId(normalized, cameraRoi);
    this.afterSelectedLaneChanged(normalized, cameraRoi);
  }

  onOpenLaneGate(lane: HeaderLane): void {
    this.sendLaneGateCommand(lane, 'open');
  }

  onCloseLaneGate(lane: HeaderLane): void {
    this.sendLaneGateCommand(lane, 'close');
  }

  private sendLaneGateCommand(lane: HeaderLane, action: GateButtonAction): void {
    if (!lane || this.isLaneGateBusy(lane.id)) {
      return;
    }

    const locationId = this.gateLocationId || getUserDataLocationId();
    if (!locationId) {
      console.warn(`Cannot ${action} gate: location id is missing from userData.`);
      return;
    }

    const text = GATE_COMMAND_TEXT[action];
    this.gateCommandsInFlight.set(lane.id, action);
    this.subscriptions.add(
      this.postGateCommand(locationId, lane.id, action)
        .pipe(
          finalize(() => {
            this.gateCommandsInFlight.delete(lane.id);
          })
        )
        .subscribe({
          next: response => {
            const payload =
              response && typeof response === 'object' && 'data' in response && response.data
                ? response.data
                : response;
            const delivered = Number(payload?.delivered ?? 0);
            const delivery = String(payload?.delivery || '');
            // Backend returns 202 only when the command reached a connected
            // controller (local SSE or another pod via Redis). Treat anything
            // else as failure so the UI does not claim the gate moved.
            const isAccepted =
              response?.status !== false &&
              payload?.status !== false &&
              (delivered > 0 || delivery === 'redis' || payload?.status === 'accepted');
            this.messageService.add({
              severity: isAccepted ? 'success' : 'error',
              summary: isAccepted ? text.success : text.failure,
              detail:
                payload?.message ||
                response?.message ||
                (isAccepted
                  ? `${lane.name} is ${text.progress} now.`
                  : 'Gate controller is offline or did not receive the command.'),
            });
          },
          error: error => {
            console.error(`Error sending ${action} gate:`, error);
            this.messageService.add({
              severity: 'error',
              summary: text.failure,
              detail:
                error?.error?.message ||
                error?.message ||
                `Unable to ${action} gate. Please try again.`,
            });
          },
        })
    );
  }

  private postGateCommand(locationId: string, laneId: string, action: GateButtonAction) {
    return this.apiService.post(
      `devices/location/${encodeURIComponent(locationId)}/lanes/${encodeURIComponent(laneId)}/${action}`,
      {}
    );
  }

  private resolveGateLocationId(): string | undefined {
    return (
      getUserDataLocationId() ||
      this.locationService.getSelectedShopLocation()?.id ||
      this.locationService.getDefaultShopLocation()?.id ||
      undefined
    );
  }

  private loadHeaderLanes(): void {
    const locationId = this.resolveGateLocationId();
    if (!locationId) {
      this.currentLanes = [];
      this.currentLaneOptions = [];
      this.selectedCurrentLane = '';
      this.gateLocationId = '';
      return;
    }
    this.gateLocationId = locationId;
    this.subscriptions.add(
      this.apiService
        .get<HeaderLane[]>(`location/view-location/${encodeURIComponent(locationId)}/lanes`)
        .subscribe({
          next: response => {
            const lanes =
              response.status && Array.isArray(response.data)
                ? response.data.filter(lane => String(lane.status || '').toLowerCase() === 'active')
                : [];
            this.applyLoadedLanes(lanes);
          },
          error: error => {
            console.error('Error loading lanes:', error);
            this.currentLanes = [];
            this.currentLaneOptions = [];
            this.selectedCurrentLane = '';
          },
        })
    );
  }

  private applyLoadedLanes(lanes: HeaderLane[]): void {
    this.currentLanes = lanes;
    this.currentLaneOptions = lanes.map(lane => lane.name);

    if (lanes.length === 0) {
      this.selectedCurrentLane = '';
      setUserDataLaneId(undefined, null);
      return;
    }

    const persistedLaneId = getUserDataLaneId();
    const persistedLane = persistedLaneId
      ? lanes.find(lane => lane.id === persistedLaneId)
      : undefined;
    const selectedLane = persistedLane || lanes[0];
    this.selectedCurrentLane = selectedLane.name;
    const cameraRoi =
      selectedLane.camera_roi != null && Number.isInteger(Number(selectedLane.camera_roi))
        ? Number(selectedLane.camera_roi)
        : null;
    setUserDataLaneId(selectedLane.id, cameraRoi);

    // Persist when no stored preference, or stored lane is no longer valid here.
    if (!persistedLane) {
      this.persistSelectedLane(selectedLane.id);
    }
  }

  private persistSelectedLane(laneId: string | null): void {
    // Route through the serialized pipeline (see persistLaneRequests$) so rapid changes
    // don't race; switchMap there cancels prior in-flight PATCHes (last-write-wins).
    this.persistLaneRequests$.next(laneId);
  }

  private getSelectedLane(): HeaderLane | undefined {
    if (!this.selectedCurrentLane) {
      return undefined;
    }
    return this.currentLanes.find(lane => lane.name === this.selectedCurrentLane);
  }

  toggleProfileDropdown() {
    this.isProfileDropdownOpen = !this.isProfileDropdownOpen;
    if (this.isProfileDropdownOpen) {
      this.closeNotificationDropdown();
    }
  }

  /**
   * Toggle the notification dropdown for mobile or desktop, close profile dropdown when opening, and synchronize notification count when closing.
   * @example
   * toggleNotificationDropdown(event)
   * undefined
   * @param {{Event=}} {{event}} - Optional DOM Event; if provided, its propagation will be stopped to prevent document click handlers from interfering.
   * @returns {{void}} No return value.
   **/
  toggleNotificationDropdown(event?: Event) {
    if (!this.showNotificationBell()) {
      return;
    }
    // Stop event propagation to prevent document click listener from interfering
    if (event) {
      event.stopPropagation();
    }

    // Close mobile nav if it's open
    if (this.isMobileNavOpen) {
      this.navigationService.setMobileNavOpen(false);
    }

    const isMobile = this.isMobileView();

    if (isMobile) {
      const wasOpen = this.isMobileNotificationDropdownOpen;
      this.isMobileNotificationDropdownOpen = !wasOpen;

      if (this.isMobileNotificationDropdownOpen) {
        // Opening: close profile dropdown
        this.isProfileDropdownOpen = false;
      }
    } else {
      this.isNotificationDropdownOpen = !this.isNotificationDropdownOpen;

      if (this.isNotificationDropdownOpen) {
        // Opening: close profile dropdown
        this.isProfileDropdownOpen = false;
      }
    }
  }

  /**
   * Increment badge for a lane Pusher event without re-fetching all lpr-entries (modal loads its own data).
   */
  private bumpNotificationBadgeForPusher(data: Record<string, any> | undefined): void {
    if (!this.showNotificationBell() || !data) {
      return;
    }
    if (isLprEntryMarkedRead(data)) {
      return;
    }
    this.notificationCount++;
  }

  /**
   * Check if there are new notifications since last viewed (from API)
   */
  checkForNewNotifications(): void {
    if (!this.showNotificationBell()) {
      this.notificationCount = 0;
      return;
    }
    // Prevent multiple simultaneous API calls
    if (this.isLoadingNotifications) {
      return;
    }

    this.isLoadingNotifications = true;
    this.locationService.getLocationIdsForCurrentUser().subscribe({
      next: (locationIds: string[]) => {
        if (locationIds.length === 0) {
          this.notificationCount = 0;
          this.isLoadingNotifications = false;
          return;
        }

        // Fetch today's LPR entries per location (header badge count only).
        const lprQueryParams = buildHeaderNotificationBadgeLprQueryParams(getUserDataLaneId());
        const requests = locationIds.map(locationId =>
          this.apiService.get(`order/lpr-entries/location/${locationId}`, lprQueryParams).pipe(
            catchError(error => {
              console.error(`Error fetching notifications for location ${locationId}:`, error);
              return of({ status: false, data: [] });
            })
          )
        );

        forkJoin(requests).subscribe({
          next: (responses: any[]) => {
            const badgeListItems = sortNotificationListItemsByTimestamp(
              responses.flatMap(response => transformLprEntriesResponseToNotificationList(response))
            );
            const badgeListItemsWithReadState = mergeKnownReadStateIntoNotificationList(
              badgeListItems,
              headerDropdownNotificationsCache.items
            );

            this.notificationCount = countUnreadNotificationListItems(badgeListItemsWithReadState);
            this.isLoadingNotifications = false;
          },
          error: (error: any) => {
            console.error('Error checking for new notifications:', error);
            this.notificationCount = 0;
            this.isLoadingNotifications = false;
          },
        });
      },
      error: (error: any) => {
        console.error('Error getting location IDs:', error);
        this.notificationCount = 0;
        this.isLoadingNotifications = false;
      },
    });
  }

  /**
   * Adjust badge when a single notification is marked read in the dropdown.
   */
  private handleNotificationReadStateChanged(event: CustomEvent<{ unreadDelta?: number }>): void {
    const delta = event.detail?.unreadDelta;
    if (typeof delta === 'number' && delta !== 0) {
      this.notificationCount = Math.max(0, this.notificationCount + delta);
      return;
    }
    this.checkForNewNotifications();
  }

  getProfileImage(): string {
    if (this.userProfile?.picture_url) {
      return this.userProfile.picture_url;
    }
    return 'assets/images/avatar.png';
  }

  get initials(): string {
    const name = this.userProfile?.name;
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length > 1) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  }

  get hasProfileImage(): boolean {
    return !(
      this.userProfile?.picture_url ||
      this.userProfile?.picture_url === '' ||
      this.userProfile?.picture_url === null ||
      this.userProfile?.picture_url === undefined ||
      this.userProfile?.picture_url === 'null'
    );
  }

  /**
   * Handle document click events to close open profile and notification dropdowns when clicking outside, excluding clicks on the notification bell or dropdown components.
   * @example
   * onDocumentClick(new MouseEvent('click'))
   * undefined
   * @param {{Event}} {{event}} - Event object from document click providing the event.target to determine click location.
   * @returns {{void}} No return value.
   **/
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const clickedInside = this.elementRef.nativeElement.contains(target);

    // Don't close if clicking on notification bell or dropdown
    const isNotificationBell = !!target?.closest('.notification-bell');
    const isNotificationDropdown = !!target?.closest('app-notification-dropdown');
    const isProfileDropdown = !!target?.closest('app-profile-dropdown');

    if (this.isProfileDropdownOpen && !clickedInside && !isProfileDropdown) {
      this.isProfileDropdownOpen = false;
    }

    // Close when clicking anywhere outside the notification list (and not the bell),
    // including other controls inside the header.
    if (
      (this.isNotificationDropdownOpen || this.isMobileNotificationDropdownOpen) &&
      !isNotificationBell &&
      !isNotificationDropdown
    ) {
      this.closeNotificationDropdown();
    }
  }

  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    if (this.isNotificationDropdownOpen || this.isMobileNotificationDropdownOpen) {
      this.closeNotificationDropdown();
    }
  }

  /**
   * Close desktop and mobile notification dropdowns.
   */
  closeNotificationDropdown(): void {
    if (!this.isNotificationDropdownOpen && !this.isMobileNotificationDropdownOpen) {
      return;
    }
    this.isNotificationDropdownOpen = false;
    this.isMobileNotificationDropdownOpen = false;
  }

  /**
   * Check if current view is mobile
   */
  isMobileView(): boolean {
    return window.innerWidth <= 880;
  }
}
